import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeletePlotGroupComparison } from '@/api/rest/resources/organization';
import { PlotGroupComparison } from '@/api/rest/resources/types/organization';
import { isNullish } from '@/utils/booleans';

export type DeleteComparisonProps = {
  comparison: PlotGroupComparison | null;
  onClose: () => void;
  onSuccess?: () => void;
};

export const DeleteComparisonWithConfirmation = ({ comparison, onClose, onSuccess }: DeleteComparisonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [deletingComparison, setDeletingComparison] = useState(false);

  const deleteComparisonMutation = useDeletePlotGroupComparison();

  const deleteComparison = async () => {
    if (!comparison) return;

    if (isNullish(comparison.id) || isNullish(comparison.organization)) {
      toast({
        type: 'error',
        title: t('global.compare.deleteComparison.toasts.error'),
      });
      return;
    }

    try {
      setDeletingComparison(true);
      await deleteComparisonMutation.mutateAsync({
        pathVariables: { organization_id: comparison.organization, plot_group_comparison_id: comparison.id },
      });

      toast({
        type: 'success',
        title: t('global.compare.deleteComparison.toasts.success'),
      });

      if (onSuccess) {
        onSuccess();
      }

      queryClient.resetQueries({ queryKey: ['organization', comparison.organization, 'plot-group-comparisons'] });

      onClose();
    } catch (error) {
      toast({
        type: 'error',
        title: t('global.compare.deleteComparison.toasts.error'),
      });
    } finally {
      setDeletingComparison(false);
    }
  };

  return (
    <Dialog open={!!comparison} onOpenChange={onClose}>
      <DialogContent data-testid='delete-comparison-with-confirmation'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('global.compare.deleteComparison.dialog.title')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          {t('global.compare.deleteComparison.dialog.message', { name: comparison?.name })}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button
            onClick={() => deleteComparison()}
            loading={deletingComparison}
            className='bg-error hover:bg-error-dark'
          >
            {t('global.compare.deleteComparison.dialog.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
